import React, { useState, useEffect, useMemo } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import AddProductModal from '../../modals/AddProductModal';
import RegionSelector, { Region } from '../../../common/RegionSelector';
import CompanySelector from '../../../common/CompanySelector';
import HeaderActions from '../../../common/HeaderActions';
import { createStandardHeaderActions } from '../../../common/HeaderActions/helpers';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import Toast from '../../../../components/Toast';

interface Product {
  _id: string;
  product_id: string;
  name: string;
  brand: string;
  global_code: string;
  amazon_asin: string;
  dimensions: string;
  base_unit: string;
  color: string;
  category: string;
  description: string;
  is_active: boolean;
  image_gridfs_id?: string;
  created_at: string;
  updated_at: string;
  supplier_count: number;
  price_range?: {
    min: number;
    max: number;
    currency: string;
  };
  last_price_update?: string;
  company_specific_codes?: Array<{
    company_id: string;
    company_name: string;
    code: string;
  }>;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ProductsDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  companyId?: string; // Optional company ID for company-specific view
  layoutType?: 'admin' | 'user'; // Layout type for DataDashboard
  secondarySidebar?: React.ReactNode; // Optional secondary sidebar
  pageDescription?: string; // Optional page description
  getDynamicSubItems?: (parentId: string, currentRoute: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
  navigationActions?: React.ReactNode;
}

const ProductsDashboard: React.FC<ProductsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  companyId,
  layoutType,
  secondarySidebar,
  pageDescription,
  getDynamicSubItems,
  navigationActions
}) => {
  const [region, setRegion] = useState<Region>('tr'); // Default to Turkey
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showAddModal, setShowAddModal] = useState(false);
  // Store current filter/search/sort state for reloading after delete
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [currentSortBy, setCurrentSortBy] = useState<string>('product_id');
  const [currentSortOrder, setCurrentSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentColumnFilters, setCurrentColumnFilters] = useState<Record<string, string>>({});
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    page: 1,
    limit: 25,
    total: 0,
    total_pages: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  // Company filter states
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  // filteredData state removed as we now filter on backend
  const [companyName, setCompanyName] = useState<string>('');

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productsToDelete, setProductsToDelete] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Base product columns definition
  const baseProductColumns = [
    {
      key: 'product_id',
      title: 'Global ID',
      type: 'text' as const,
      sortable: true,
      width: '100px',
      editable: false
    },
    {
      key: 'image_gridfs_id',
      title: 'Image',
      type: 'image' as const,
      sortable: false,
      width: '80px',
      editable: false
    },
    {
      key: 'name',
      title: 'Product Name',
      type: 'text' as const,
      sortable: true,
      width: '200px',
      editable: true
    },
    {
      key: 'brand',
      title: 'Brand',
      type: 'text' as const,
      sortable: true,
      width: '120px',
      editable: true
    },
    {
      key: 'category',
      title: 'Category',
      type: 'categories' as const,
      sortable: true,
      width: '150px',
      editable: true
    },
    {
      key: 'global_code',
      title: 'Global Code',
      type: 'text' as const,
      sortable: true,
      width: '150px',
      editable: true
    },
    {
      key: 'amazon_asin',
      title: 'Amazon ASIN',
      type: 'text' as const,
      sortable: true,
      width: '120px',
      editable: true
    },
    {
      key: 'dimensions',
      title: 'Dimensions',
      type: 'text' as const,
      sortable: true,
      width: '120px',
      editable: true
    },
    {
      key: 'base_unit',
      title: 'Base Unit',
      type: 'dropdown' as const,
      sortable: true,
      width: '100px',
      editable: true,
      options: ['kg', 'gr', 'lt', 'ml', 'metre', 'mm', 'adet', 'kutu', 'paket']
    },
    {
      key: 'color',
      title: 'Color',
      type: 'text' as const,
      sortable: true,
      width: '100px',
      editable: true
    },
    {
      key: 'supplier_count',
      title: 'Supplier Count',
      type: 'number' as const,
      sortable: true,
      width: '120px',
      editable: false
    },
    {
      key: 'price_range',
      title: 'Price Range',
      type: 'price_range' as const,
      sortable: false,
      width: '150px',
      editable: false
    },
    {
      key: 'last_price_update',
      title: 'Last Price Update',
      type: 'date' as const,
      sortable: true,
      width: '150px',
      editable: false
    },
    {
      key: 'company_specific_codes',
      title: 'Company Codes',
      type: 'companies' as const,
      sortable: false,
      width: '200px',
      editable: true
    },
  ];

  // Dynamic columns based on selected companies
  const productColumns = useMemo(() => {
    const cols: any[] = [...baseProductColumns];

    // Add dynamic company code columns for each selected company
    const companyCodesIndex = cols.findIndex(col => col.key === 'company_specific_codes');

    if (selectedCompanies.length > 0) {
      // Remove the generic company_specific_codes column
      if (companyCodesIndex !== -1) {
        cols.splice(companyCodesIndex, 1);
      }

      // Add individual columns for each selected company
      selectedCompanies.forEach((company, index) => {
        cols.splice(companyCodesIndex !== -1 ? companyCodesIndex + index : cols.length - 1, 0, {
          key: `company_code_${company.company_id}`,
          title: `${company.company_name} Code`,
          type: 'text' as const,
          sortable: false,
          editable: true,
          width: '150px',
          render: (value: any, row: any) => {
            const companyCodes = row.company_specific_codes || [];
            const companyCode = companyCodes.find((c: any) => {
              return c.company_id === company.company_id;
            });
            return companyCode?.code || '';
          }
        });
      });
    }

    // Add status column only for global admins
    if (user?.role === 'admin') {
      cols.push({
        key: 'is_active',
        title: 'Status',
        type: 'status' as const,
        sortable: true,
        width: '100px',
        editable: true
      });
    }

    return cols;
  }, [selectedCompanies, baseProductColumns, user]);

  const loadProducts = async (
    page: number = 1,
    limit: number = 25,
    search: string = '',
    sortBy: string = 'product_id',
    sortOrder: 'asc' | 'desc' = 'asc',
    columnFilters: Record<string, string> = {},
    explicitCompanyIds?: string[]
  ) => {
    if (!token) return;

    try {
      setError(null);
      const params = new URLSearchParams({
        region: region, // Add region parameter
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      // Add company_id filter if companyId is provided (for company-specific view)
      if (companyId) {
        params.append('company_id', companyId);
      }

      if (search) params.append('search', search);

      // Add column filters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value.trim()) {
          params.append(`filter_${key}`, value.trim());
        }
      });

      // Add company_ids filter
      // Use explicit list if provided (e.g. on Apply click), otherwise check isFiltered state
      const companiesToFilter = explicitCompanyIds || (isFiltered ? selectedCompanies.map(c => c.company_id) : []);
      if (companiesToFilter.length > 0) {
        params.append('company_ids', companiesToFilter.join(','));
      }

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS_V2}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setProducts(result.data);
        setPaginationInfo(result.pagination);

        // Removed client-side filtering logic as backend now handles it
      } else {
        throw new Error(result.error || 'Failed to load products');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError(error instanceof Error ? error.message : 'Failed to load products');
    }
  };

  const handleDataFetch = (params: {
    page: number;
    limit: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  }) => {
    // Store current filter state
    setCurrentSearch(params.search || '');
    setCurrentSortBy(params.sortBy || 'product_id');
    setCurrentSortOrder((params.sortOrder as 'asc' | 'desc') || 'asc');
    setCurrentColumnFilters(params.columnFilters || {});

    loadProducts(
      params.page,
      params.limit,
      params.search || '',
      params.sortBy || 'product_id',
      (params.sortOrder as 'asc' | 'desc') || 'asc',
      params.columnFilters || {},
      isFiltered ? selectedCompanies.map(c => c.company_id) : undefined
    );
  };

  const handleEditProduct = async (id: string, field: string, value: any, row?: any) => {
    try {
      // Check if this is a dynamic company code column
      if (field.startsWith('company_code_')) {
        const companyId = field.replace('company_code_', '');
        const company = selectedCompanies.find(c => c.company_id === companyId);

        if (!company) {
          console.error('Company not found for ID:', companyId);
          return;
        }

        // Get current product
        const product = products.find(p => p._id === id);
        if (!product) {
          console.error('Product not found for ID:', id);
          return;
        }

        // Update company_specific_codes array
        let companyCodes = product.company_specific_codes || [];
        const existingCodeIndex = companyCodes.findIndex(c => c.company_id === companyId);

        if (existingCodeIndex !== -1) {
          // Update existing code
          companyCodes[existingCodeIndex].code = value;
        } else {
          // Add new code
          companyCodes.push({
            company_id: companyId,
            company_name: company.company_name,
            code: value
          });
        }

        // Send update to backend with company_specific_codes
        const response = await fetch(`${API_ENDPOINTS.PRODUCT_DETAILS(id)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_specific_codes: companyCodes })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          // Update local state
          setProducts(prevProducts =>
            prevProducts.map(product =>
              product._id === id
                ? { ...product, company_specific_codes: companyCodes, updated_at: new Date().toISOString() }
                : product
            )
          );
        } else {
          throw new Error(result.error || 'Failed to update product');
        }
      } else {
        // Normal field edit
        const response = await fetch(`${API_ENDPOINTS.PRODUCT_DETAILS(id)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ [field]: value })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          // Update local state
          setProducts(prevProducts =>
            prevProducts.map(product =>
              product._id === id
                ? { ...product, [field]: value, updated_at: new Date().toISOString() }
                : product
            )
          );
        } else {
          throw new Error(result.error || 'Failed to update product');
        }
      }
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // Cancel editing - reset any pending changes
      setSelectedProducts([]);
    }
  };

  const handleDeleteProducts = async (productIds: string[]) => {
    // Check if user is a top-level admin (role=admin, no company_id)
    const isTopLevelAdmin = user?.role === 'admin' && !user?.company_id && !companyId;

    // For top-level admins, delete products completely (no company_id needed)
    // For regular users or company admins, remove company codes (company_id required)
    let targetCompanyId: string | null = null;
    if (!isTopLevelAdmin) {
      targetCompanyId = companyId || (user?.company_id ? String(user.company_id) : null);

      if (!targetCompanyId) {
        setToastMessage('Company ID is required to remove company codes');
        setToastType('error');
        setShowToast(true);
        return;
      }
    }

    try {
      const requestBody: any = {
        ids: productIds,
        region: region // Include region to use correct collection
      };

      // Only include company_id if not top-level admin
      if (!isTopLevelAdmin && targetCompanyId) {
        requestBody.company_id = targetCompanyId;
      }

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS_V2}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle error response
        const errorMessage = result.error || result.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      if (result.success) {
        // Reload products to get updated data, preserving current filters
        await loadProducts(
          paginationInfo.page,
          paginationInfo.limit,
          currentSearch, // Use current search
          currentSortBy, // Use current sort
          currentSortOrder, // Use current sort order
          currentColumnFilters, // Use current column filters
          isFiltered ? selectedCompanies.map(c => c.company_id) : undefined // Use current company filter
        );

        setToastMessage(`Successfully removed company codes from ${result.removed_count || productIds.length} product(s)`);
        setToastType('success');
        setShowToast(true);

        // Clear selection
        setSelectedProducts([]);
      } else {
        throw new Error(result.error || result.message || 'Failed to remove company codes');
      }
    } catch (error) {
      console.error('Error deleting products:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove company codes';
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
      throw error;
    }
  };

  const handleExport = async (selectedIds?: string[]) => {
    try {
      const idsToExport = selectedIds && selectedIds.length > 0 ? selectedIds : products.map(p => p._id);

      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          collection: 'products',
          fields: productColumns.map(col => col.key),
          filters: {
            '_id': { '$in': idsToExport }
          },
          format: 'excel',
          filename: 'products_export'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      a.download = `products_export_${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting products:', error);
      throw error;
    }
  };

  const handleSaveChanges = async () => {
    setIsEditing(false);
    setSelectedProducts([]);
  };

  const handleDelete = async () => {
    if (selectedProducts.length === 0) return;

    // Show confirmation modal
    setProductsToDelete([...selectedProducts]);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await handleDeleteProducts(productsToDelete);
      setProductsToDelete([]);
    } catch (error) {
      console.error('Error deleting products:', error);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    // Reload data with new page size
    handleDataFetch({
      page: 1,
      limit: newItemsPerPage,
      search: '',
      sortBy: 'product_id',
      sortOrder: 'asc',
      columnFilters: {}
    });
  };

  const handleAddProduct = () => {
    setShowAddModal(true);
  };

  // Get user's company info for auto-selecting in modal
  const [userCompanyInfo, setUserCompanyInfo] = useState<{ companyId?: string; companyName?: string }>({});

  useEffect(() => {
    const loadUserCompanyInfo = async () => {
      // If companyId prop is provided, use it
      if (companyId && companyName) {
        setUserCompanyInfo({ companyId, companyName });
        return;
      }

      // Otherwise, get from user object
      if (user?.company_id) {
        const userCompanyId = typeof user.company_id === 'string' ? user.company_id : user.company_id._id || user.company_id;
        let userCompanyName = user.company?.name || user.company_name || '';

        // If company name is not available, fetch it
        if (!userCompanyName && userCompanyId && token) {
          try {
            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${userCompanyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                userCompanyName = data.data.name || '';
              }
            }
          } catch (error) {
            console.error('Error loading user company info:', error);
          }
        }

        setUserCompanyInfo({ companyId: userCompanyId, companyName: userCompanyName });
      } else {
        setUserCompanyInfo({});
      }
    };

    loadUserCompanyInfo();
  }, [user, companyId, companyName, token]);

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleProductAdded = (newProduct?: any) => {
    // Refresh the products list
    handleDataFetch({
      page: paginationInfo.page,
      limit: paginationInfo.limit,
      search: '',
      sortBy: 'product_id',
      sortOrder: 'asc',
      columnFilters: {}
    });
    setShowAddModal(false);
  };

  const handleOpen = () => {
    if (selectedProducts.length === 1) {
      const selectedProductId = selectedProducts[0]; // This is the _id from products collection

      // Route to company-specific product details if companyId exists
      const navigationUrl = companyId
        ? `/dashboard/companies/${companyId}/product-details?product_id=${selectedProductId}`
        : `/dashboard/products/details?product_id=${selectedProductId}`;

      // Navigate to Product Details with the selected product's _id
      onNavigate(navigationUrl);
    }
  };

  const handleSelectionChange = (newSelectedProducts: string[]) => {
    setSelectedProducts(newSelectedProducts);
  };

  // Load company info if companyId is provided
  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!companyId || !token) return;

      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const company = { company_id: companyId, company_name: data.data.name };
            setCompanyName(data.data.name);
            setSelectedCompanies([company]);
            // Auto-apply filter for company-specific view
            setIsFiltered(true);
          }
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };

    loadCompanyInfo();
  }, [companyId, token]);



  useEffect(() => {
    if (token) {
      loadProducts();
    }
  }, [token, region]); // Re-load when region changes

  // Handle region change
  const handleRegionChange = (newRegion: Region) => {
    setRegion(newRegion);
    // Data will auto-reload via useEffect
  };

  // Handle company selection
  const handleCompanyChange = (companies: Array<{ company_id: string; company_name: string }>) => {
    setSelectedCompanies(companies);
  };

  // Handle apply filter
  const handleApplyFilter = () => {
    if (selectedCompanies.length === 0) {
      setIsFiltered(false);
      loadProducts(1, itemsPerPage, currentSearch, currentSortBy, currentSortOrder, currentColumnFilters, []);
      return;
    }

    setIsFiltered(true);
    // Force reload with server-side filter
    // We pass explicit list because isFiltered state update might not be reflected yet
    const companyIds = selectedCompanies.map(c => c.company_id);
    loadProducts(1, itemsPerPage, currentSearch, currentSortBy, currentSortOrder, currentColumnFilters, companyIds);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setIsFiltered(false);
    // Reload without company filters
    loadProducts(1, itemsPerPage, currentSearch, currentSortBy, currentSortOrder, currentColumnFilters, []);
  };

  // Check if user has permission to add products (admin, useradmin, or edit_product permission)
  const canAddProducts = useMemo(() => {
    const userRole = user?.role;
    const userPermissions = user?.permissions || {};
    const hasEditProduct = userPermissions?.edit_product === true || userPermissions?.edit_product === 'true';

    return userRole === 'admin' || userRole === 'useradmin' || hasEditProduct;
  }, [user]);

  // Header actions for user layout (region selector in header)
  const isUserLayout = layoutType === 'user';

  // Create custom header actions with region selector for user layout
  const userHeaderActions = isUserLayout ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <RegionSelector value={region} onChange={handleRegionChange} />
    </div>
  ) : undefined;

  const headerContent = isUserLayout ? undefined : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', position: 'relative', overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Region selector only for admin layout and not company specific */}
        {!isUserLayout && !companyId && (
          <RegionSelector value={region} onChange={handleRegionChange} />
        )}

        {/* Company Selector (disabled if companyId is provided, only for admin) */}
        {!companyId && !isUserLayout && (
          <div style={{ position: 'relative', zIndex: 10001, display: 'inline-block' }}>
            <CompanySelector
              value={selectedCompanies}
              onChange={handleCompanyChange}
              placeholder="Select companies to see their codes..."
              multiple={true}
            />
          </div>
        )}

        {/* Viewing products from text only for admin layout */}
        {!isUserLayout && (
          <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
            Viewing products from: <strong>{region.toUpperCase()}</strong>
          </span>
        )}

        {/* Apply Filter Button (only in global view) */}
        {!companyId && selectedCompanies.length > 0 && !isFiltered && (
          <button
            onClick={handleApplyFilter}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Apply Filter
          </button>
        )}

        {/* Clear Filter Button (only in global view) */}
        {!companyId && isFiltered && (
          <button
            onClick={handleClearFilter}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Selected Companies Display */}
      {selectedCompanies.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef'
        }}>
          {selectedCompanies.map((company) => (
            <span
              key={company.company_id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                backgroundColor: '#e3f2fd',
                color: '#1565c0',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500'
              }}
            >
              {company.company_name}
              {/* Only show remove button in global view */}
              {!companyId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCompanies(selectedCompanies.filter(c => c.company_id !== company.company_id));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1565c0',
                    cursor: 'pointer',
                    padding: '0',
                    fontSize: '14px',
                    lineHeight: '1',
                    marginLeft: '2px'
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="admin-v2-dashboard-container">
        <div className="loading-state">
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-v2-dashboard-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={() => loadProducts()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="admin-v2-dashboard-container">
        <DataDashboard
          title="Products"
          pageDescription={pageDescription}
          headerContent={headerContent}
          customHeaderActions={isUserLayout ? (
            <HeaderActions
              {...createStandardHeaderActions({
                isEditing,
                onEditToggle: handleEditToggle,
                onSaveChanges: handleSaveChanges,
                onExport: handleExport,
                onDelete: handleDelete,
                selectedItemsCount: selectedProducts.length,
                itemsPerPage,
                onItemsPerPageChange: handleItemsPerPageChange,
                onFiltersToggle: () => { },
                useIndividualComponents: true,
                showEditButton: true,
                showDeleteButton: true,
                showExportButton: true,
                onOpen: () => handleOpen(),
              })}
              customActions={
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {!companyId && <RegionSelector value={region} onChange={handleRegionChange} />}
                </div>
              }
            />
          ) : undefined}
          data={products}
          columns={productColumns}
          onDataFetch={handleDataFetch}
          paginationInfo={paginationInfo}
          onEdit={handleEditProduct}
          onDelete={handleDelete}
          onExport={handleExport}
          collection="products"
          showSearch={true}
          showAddButton={canAddProducts}
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          onDataChange={setProducts}
          isEditing={isEditing}
          onEditToggle={handleEditToggle}
          onSaveChanges={handleSaveChanges}
          selectedItems={selectedProducts}
          onSelectionChange={handleSelectionChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          onOpen={handleOpen}
          onAdd={handleAddProduct}
          showEditButton={true}
          showDeleteButton={true}
          AddModal={AddProductModal}
          showSelection={true}
          getDynamicSubItems={getDynamicSubItems}
          navigationActions={navigationActions}
          layoutType={layoutType}
          secondarySidebar={secondarySidebar}
        />

        {/* Add Product Modal */}
        <AddProductModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSuccess={handleProductAdded}
          companyId={userCompanyInfo.companyId}
          companyName={userCompanyInfo.companyName}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProductsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title={(user?.role === 'admin' && !user?.company_id && !companyId) ? "Delete Products" : "Remove Company Codes"}
        message={
          (user?.role === 'admin' && !user?.company_id && !companyId)
            ? `Are you sure you want to delete ${productsToDelete.length} product(s)? This action cannot be undone.`
            : `Are you sure you want to remove company codes from ${productsToDelete.length} selected product(s)? This will only remove the company-specific codes, not the products themselves.`
        }
        confirmText={(user?.role === 'admin' && !user?.company_id && !companyId) ? "Delete" : "Remove Codes"}
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </>
  );
};

export default ProductsDashboard;
